import React, { useEffect, useRef, useState } from 'react';
import { ResourcePlanningProvider, ResourceWorkspace } from './ResourcePlanning';
import MdsTaskTemplates from './MdsTaskTemplates';
import './modernized_app.css';
import { MDS_OPTIONS, MILITARY_ROLE_OPTIONS, MILITARY_SHOP_OPTIONS, PERSONNEL_SKILL_LEVEL_OPTIONS } from './militaryOptions';

const formatLocalDateTime = value => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const centsToDollars = value => {
  if (value === null || value === undefined || value === '') return '';
  return (Number(value) / 100).toFixed(2);
};

const formatCurrencyFromCents = value => {
  const dollars = centsToDollars(value);
  return dollars ? `$${dollars}` : '$0.00';
};

const formatMonthlyPrice = value => `${formatCurrencyFromCents(value)}/mo`;

const formatDeploymentMode = value => ({
  mfe_hosted_saas: 'MFE-hosted SaaS',
  customer_owned: 'Customer-owned deployment',
  hybrid: 'Hybrid / transition',
}[value] || value || 'MFE-hosted SaaS');

const formatLocationSharingDetail = row => {
  if (row.lastLocationAt) return `Last shared ${formatLocalDateTime(row.lastLocationAt)}`;
  if (row.lastLoginAt) return `Last login ${formatLocalDateTime(row.lastLoginAt)}`;
  return 'No login activity recorded';
};

const normalizeRoleValue = value => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

const formatTemplateNumber = value => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return '0';
  return numeric.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

const modelRequirementLabel = requirement => {
  const noun = String(requirement?.noun || '').trim() || 'Unnamed 9188-6 job';
  const wuc = String(requirement?.wuc || '').trim();
  return wuc ? `${noun} | ${wuc}` : noun;
};

const emptyLaborPoolDraft = selectedBase => ({
  profileScope: 'location',
  location: selectedBase || '',
  name: '',
  eligibleShops: [],
  notes: '',
  active: true,
});

const laborPoolDraftFromRow = (row, selectedBase) => ({
  profileScope: String(row?.location || '').trim() ? 'location' : 'enterprise',
  location: row?.location || selectedBase || '',
  name: row?.name || '',
  eligibleShops: row?.eligibleShops || [],
  notes: row?.notes || '',
  active: row?.active !== false,
});

export default function AdminTab(props) {
  return <ResourcePlanningProvider api={props.api} user={props.user} location={props.selectedBase || props.user?.location} globalScope={Boolean(props.user?.is_superuser)}><AdminTabContent {...props} /></ResourcePlanningProvider>;
}

function AdminTabContent({ api, user, selectedBase }) {
  const normalizedRole = normalizeRoleValue(user?.role);
  const normalizedShop = String(user?.shop || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  const canManageUsers = Boolean(user?.is_superuser || normalizedRole === 'siteadmin' || normalizedRole === 'admin');
  const canManageTemplates = Boolean(user?.is_superuser || ['siteadmin', 'management', 'production'].includes(normalizedRole) || normalizedShop === 'production');
  const canUseAdmin = Boolean(
    canManageUsers ||
    ['commander', 'management', 'production'].includes(normalizedRole) ||
    normalizedShop === 'production'
  );
  const [activeSection, setActiveSection] = useState(canManageUsers ? 'users' : 'subscription');
  const [users, setUsers] = useState([]);
  const [roleOptions, setRoleOptions] = useState(MILITARY_ROLE_OPTIONS);
  const [shopOptions, setShopOptions] = useState(MILITARY_SHOP_OPTIONS);
  const [drafts, setDrafts] = useState({});
  const [transferRequests, setTransferRequests] = useState([]);
  const [transferDrafts, setTransferDrafts] = useState({});
  const [reviewingTransferId, setReviewingTransferId] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [billingCatalog, setBillingCatalog] = useState(null);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [billingAction, setBillingAction] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [savingUserId, setSavingUserId] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('active');
  const [laborTemplates, setLaborTemplates] = useState([]);
  const [laborTemplateSubsection, setLaborTemplateSubsection] = useState('templates');
  const [laborTemplatesLoading, setLaborTemplatesLoading] = useState(false);
  const [laborPools, setLaborPools] = useState([]);
  const [editingLaborPoolId, setEditingLaborPoolId] = useState('');
  const [laborPoolDraft, setLaborPoolDraft] = useState(emptyLaborPoolDraft(selectedBase));
  const [savingLaborPoolId, setSavingLaborPoolId] = useState('');
  const [modelAircraftRows, setModelAircraftRows] = useState([]);
  const [modelSourceLocationFilter, setModelSourceLocationFilter] = useState('all');
  const [modelSourceSearch, setModelSourceSearch] = useState('');
  const [modelSourceAircraftId, setModelSourceAircraftId] = useState('');
  const [modelTargetAircraftIds, setModelTargetAircraftIds] = useState([]);
  const [modelPreview, setModelPreview] = useState(null);
  const [selectedModelSuggestionIds, setSelectedModelSuggestionIds] = useState([]);
  const [modelSourceRequirementFilter, setModelSourceRequirementFilter] = useState('');
  const [modelSourceRequirements, setModelSourceRequirements] = useState([]);
  const [modelSourceRequirementsLoading, setModelSourceRequirementsLoading] = useState(false);
  const [modelApplyResources, setModelApplyResources] = useState(true);
  const [modelOverwriteResources, setModelOverwriteResources] = useState(false);
  const [modelOverwriteLabor, setModelOverwriteLabor] = useState(false);
  const [modelRememberReadiness, setModelRememberReadiness] = useState(true);
  const [modelApplyCoverage, setModelApplyCoverage] = useState(true);
  const [modelReadinessLearningScope, setModelReadinessLearningScope] = useState('mds');
  const [modelAircraftWorking, setModelAircraftWorking] = useState('');
  const modelApplyInFlightRef = useRef(false);
  const modelSourceRequirementsRequestRef = useRef(0);

  const loadLaborTemplateRepository = async () => {
    if (!api || !user?.email || !canManageTemplates) return;
    try {
      setLaborTemplatesLoading(true);
      setError('');
      const params = {
        email: user.email,
        location: selectedBase || user.location || '',
        global: Boolean(user.is_superuser),
      };
      const [templateRes, poolRes] = await Promise.all([
        api.get('/g081-imports/labor-templates', { params }),
        api.get('/g081-imports/labor-pools', { params }),
      ]);
      setLaborTemplates(templateRes.data.templates || []);
      setLaborPools(poolRes.data.pools || templateRes.data.laborPools || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load MDS labor task templates.');
    } finally {
      setLaborTemplatesLoading(false);
    }
  };

  const laborTemplateRequestContext = () => ({
    email: user.email,
    location: selectedBase || user.location || '',
    global: Boolean(user.is_superuser),
  });

  const loadLaborPools = async () => {
    if (!api || !user?.email || !canManageTemplates) return;
    try {
      setLaborTemplatesLoading(true);
      setError('');
      const res = await api.get('/g081-imports/labor-pools', {
        params: {
          email: user.email,
          location: selectedBase || user.location || '',
          global: Boolean(user.is_superuser),
        },
      });
      setLaborPools(res.data.pools || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load labor pools.');
    } finally {
      setLaborTemplatesLoading(false);
    }
  };

  const loadModelAircraftOptions = async () => {
    if (!api || !user?.email || !user?.is_superuser) return;
    try {
      setModelAircraftWorking('options');
      setError('');
      const res = await api.get('/g081-imports/model-aircraft/options', {
        params: laborTemplateRequestContext(),
      });
      const aircraftRows = res.data.aircraft || [];
      setModelAircraftRows(aircraftRows);
      setModelSourceLocationFilter(current => (
        current === 'all' && selectedBase && aircraftRows.some(row => row.location === selectedBase)
          ? selectedBase
          : current
      ));
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load model aircraft options.');
    } finally {
      setModelAircraftWorking('');
    }
  };

  const loadModelSourceRequirements = async aircraftId => {
    const requestId = modelSourceRequirementsRequestRef.current + 1;
    modelSourceRequirementsRequestRef.current = requestId;
    if (!aircraftId || !api || !user?.email || !user?.is_superuser) {
      setModelSourceRequirements([]);
      setModelSourceRequirementsLoading(false);
      return;
    }
    try {
      setModelSourceRequirementsLoading(true);
      const res = await api.get(`/g081-imports/model-aircraft/${aircraftId}/requirements`, {
        params: laborTemplateRequestContext(),
      });
      if (modelSourceRequirementsRequestRef.current !== requestId) return;
      const requirements = res.data.requirements || [];
      setModelSourceRequirements(requirements);
      setModelSourceRequirementFilter(current => (
        current && requirements.some(row => row.id === current) ? current : ''
      ));
    } catch (err) {
      if (modelSourceRequirementsRequestRef.current === requestId) {
        setModelSourceRequirements([]);
        setError(err.response?.data?.message || 'Unable to load model tail tasks.');
      }
    } finally {
      if (modelSourceRequirementsRequestRef.current === requestId) {
        setModelSourceRequirementsLoading(false);
      }
    }
  };

  const loadSubscriptionForLocation = async location => {
    if (!api || !user?.email || !location || !canUseAdmin) return null;
    const subscriptionRes = await api.get('/billing/plans', {
      params: {
        email: user.email,
        location,
      },
    });
    const subscriptionRow = subscriptionRes.data.subscription || null;
    setSubscription(subscriptionRow);
    setBillingCatalog(subscriptionRes.data.catalog || null);
    return subscriptionRow;
  };

  const startBillingCheckout = async planKey => {
    if (!planKey) return;
    try {
      setBillingAction(planKey);
      setError('');
      setMessage('');
      const res = await api.post('/billing/create-checkout-session', {
        email: user.email,
        location: selectedBase,
        planKey,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        setError('Checkout did not return a billing link.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to start billing checkout.');
    } finally {
      setBillingAction('');
    }
  };

  const openBillingPortal = async () => {
    try {
      setBillingAction('portal');
      setError('');
      setMessage('');
      const res = await api.post('/billing/create-portal-session', {
        email: user.email,
        location: selectedBase,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        setError('Billing portal did not return a link.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to open billing portal.');
    } finally {
      setBillingAction('');
    }
  };

  const loadAdminData = async () => {
    if (!api || !user?.email || !selectedBase || !canUseAdmin) return;
    try {
      setIsLoading(true);
      setError('');
      if (canManageUsers) {
        const usersRes = await api.get('/admin/users', {
          params: {
            email: user.email,
            location: selectedBase,
          },
        });

        const rows = usersRes.data.data || [];
        setUsers(rows);
        setRoleOptions(usersRes.data.roleOptions || MILITARY_ROLE_OPTIONS);
        setShopOptions(usersRes.data.shopOptions || MILITARY_SHOP_OPTIONS);
        setDrafts(Object.fromEntries(rows.map(row => [
          row.id,
          {
            role: row.role || '',
            shop: row.shop || '',
          },
        ])));
        const transferRes = await api.get('/admin/location-transfer-requests', {
          params: {
            email: user.email,
            location: selectedBase,
          },
        });
        const transferRows = transferRes.data.data || [];
        setTransferRequests(transferRows);
        setTransferDrafts(Object.fromEntries(transferRows.map(row => [
          row.id,
          {
            role: row.requestedRole || row.currentRole || '',
            shop: row.requestedShop || row.currentShop || '',
            reviewNote: '',
          },
        ])));
      } else {
        setUsers([]);
        setDrafts({});
        setTransferRequests([]);
        setTransferDrafts({});
        setActiveSection('subscription');
      }

      try {
        await loadSubscriptionForLocation(selectedBase);
      } catch (billingErr) {
        setSubscription(null);
        setError(billingErr.response?.data?.message || 'The subscription recap is not available yet.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load admin users.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAdminData();
  }, [api, user?.email, user?.role, user?.shop, user?.is_superuser, selectedBase]);

  useEffect(() => {
    if (activeSection === 'laborTemplates') {
      loadLaborTemplateRepository();
    }
  }, [activeSection, api, user?.email, user?.is_superuser, selectedBase]);

  useEffect(() => {
    if (activeSection === 'laborTemplates' && laborTemplateSubsection === 'model') {
      loadModelAircraftOptions();
    }
  }, [activeSection, laborTemplateSubsection, api, user?.email, user?.is_superuser, selectedBase]);

  useEffect(() => {
    if (!canManageUsers && ['users', 'transfers'].includes(activeSection)) {
      setActiveSection('subscription');
    }
  }, [canManageUsers, activeSection]);

  const updateDraft = (userId, field, value) => {
    setDrafts(prev => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || {}),
        [field]: value,
      },
    }));
    setMessage('');
    setError('');
  };

  const saveUser = async row => {
    const draft = drafts[row.id] || {};
    try {
      setSavingUserId(row.id);
      setMessage('');
      setError('');
      const res = await api.patch(`/admin/users/${row.id}`, {
        email: user.email,
        location: selectedBase,
        role: draft.role,
        shop: draft.shop,
      });
      const updated = res.data.user;
      setUsers(prev => prev.map(item => item.id === updated.id ? updated : item));
      setDrafts(prev => ({
        ...prev,
        [updated.id]: {
          role: updated.role || '',
          shop: updated.shop || '',
        },
      }));
      setMessage(res.data.message || 'User updated.');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save user changes.');
    } finally {
      setSavingUserId('');
    }
  };

  const setUserActive = async (row, nextActive) => {
    const action = nextActive ? 'reactivate' : 'deactivate';
    if (!nextActive && !window.confirm(`Deactivate ${row.email}? They will not be able to log in.`)) return;
    const deactivationReason = nextActive
      ? ''
      : window.prompt('Optional reason for deactivation:', 'Member left the unit') || '';

    try {
      setSavingUserId(row.id);
      setMessage('');
      setError('');
      const res = await api.patch(`/admin/users/${row.id}`, {
        email: user.email,
        location: selectedBase,
        isActive: nextActive,
        deactivationReason,
      });
      const updated = res.data.user;
      setUsers(prev => prev.map(item => item.id === updated.id ? updated : item));
      setMessage(`${row.email} ${action}d.`);
    } catch (err) {
      setError(err.response?.data?.message || `Unable to ${action} user.`);
    } finally {
      setSavingUserId('');
    }
  };

  const updateTransferDraft = (requestId, field, value) => {
    setTransferDrafts(prev => ({
      ...prev,
      [requestId]: {
        ...(prev[requestId] || {}),
        [field]: value,
      },
    }));
    setMessage('');
    setError('');
  };

  const reviewTransferRequest = async (row, action) => {
    const draft = transferDrafts[row.id] || {};
    if (action === 'approve' && !window.confirm(`Approve transfer for ${row.email} to ${selectedBase}? Their old location access will be replaced.`)) {
      return;
    }
    if (action === 'reject' && !window.confirm(`Reject transfer request for ${row.email}?`)) {
      return;
    }

    try {
      setReviewingTransferId(row.id);
      setMessage('');
      setError('');
      const res = await api.patch(`/admin/location-transfer-requests/${row.id}`, {
        email: user.email,
        location: selectedBase,
        action,
        role: draft.role,
        shop: draft.shop,
        reviewNote: draft.reviewNote,
      });
      setTransferRequests(prev => prev.filter(item => item.id !== row.id));
      if (res.data?.user) {
        setUsers(prev => [res.data.user, ...prev.filter(item => item.id !== res.data.user.id)]);
        setDrafts(prev => ({
          ...prev,
          [res.data.user.id]: {
            role: res.data.user.role || '',
            shop: res.data.user.shop || '',
          },
        }));
      }
      if (res.data?.subscription) {
        setSubscription(res.data.subscription);
      } else {
        await loadSubscriptionForLocation(selectedBase);
      }
      setMessage(res.data?.message || `Transfer ${action}d.`);
    } catch (err) {
      setError(err.response?.data?.message || `Unable to ${action} transfer request.`);
    } finally {
      setReviewingTransferId('');
    }
  };

  const updateSupportAccess = async nextEnabled => {
    const promptText = nextEnabled
      ? 'Optional reason for enabling MFE support access:'
      : 'Optional reason for revoking MFE support access:';
    const defaultReason = nextEnabled ? 'Customer support / troubleshooting access granted' : 'Support access no longer needed';
    const reason = window.prompt(promptText, defaultReason);
    if (reason === null) return;
    if (!nextEnabled && !window.confirm('Revoke MFE support access for this location?')) return;

    try {
      setBillingAction(nextEnabled ? 'support-enable' : 'support-revoke');
      setMessage('');
      setError('');
      const res = await api.patch('/admin/location-support-access', {
        email: user.email,
        location: selectedBase,
        enabled: nextEnabled,
        reason: reason || '',
      });
      setSubscription(res.data.subscription || null);
      setMessage(res.data.message || 'MFE support access updated.');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update MFE support access.');
    } finally {
      setBillingAction('');
    }
  };

  const updateLaborPoolDraft = (field, value) => {
    setLaborPoolDraft(prev => ({ ...prev, [field]: value }));
    setMessage('');
    setError('');
  };

  const startNewLaborPool = () => {
    setEditingLaborPoolId('new');
    setLaborPoolDraft(emptyLaborPoolDraft(selectedBase));
    setMessage('');
    setError('');
  };

  const startEditLaborPool = row => {
    setEditingLaborPoolId(row.id);
    setLaborPoolDraft(laborPoolDraftFromRow(row, selectedBase));
    setMessage('');
    setError('');
  };

  const cancelLaborPoolEdit = () => {
    setEditingLaborPoolId('');
    setLaborPoolDraft(emptyLaborPoolDraft(selectedBase));
  };

  const laborPoolPayload = () => ({
    ...laborTemplateRequestContext(),
    location: laborPoolDraft.profileScope === 'enterprise' ? '' : laborPoolDraft.location,
    name: laborPoolDraft.name,
    eligibleShops: laborPoolDraft.eligibleShops,
    notes: laborPoolDraft.notes,
    active: laborPoolDraft.active,
  });

  const saveLaborPool = async event => {
    event.preventDefault();
    const savingId = editingLaborPoolId || 'new';
    try {
      setSavingLaborPoolId(savingId);
      setMessage('');
      setError('');
      const payload = laborPoolPayload();
      const res = editingLaborPoolId === 'new'
        ? await api.post('/g081-imports/labor-pools', payload)
        : await api.patch(`/g081-imports/labor-pools/${editingLaborPoolId}`, payload);
      const pool = res.data.pool;
      setLaborPools(prev => (
        editingLaborPoolId === 'new'
          ? [pool, ...prev]
          : prev.map(row => row.id === pool.id ? pool : row)
      ));
      setEditingLaborPoolId('');
      setLaborPoolDraft(emptyLaborPoolDraft(selectedBase));
      setMessage('Labor pool saved.');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save labor pool.');
    } finally {
      setSavingLaborPoolId('');
    }
  };

  const deleteLaborPool = async row => {
    if (!window.confirm(`Delete labor pool "${row?.name || 'Untitled pool'}"?`)) return;
    try {
      setSavingLaborPoolId(row.id);
      setMessage('');
      setError('');
      await api.delete(`/g081-imports/labor-pools/${row.id}`, { data: laborTemplateRequestContext() });
      setLaborPools(prev => prev.filter(item => item.id !== row.id));
      if (editingLaborPoolId === row.id) {
        cancelLaborPoolEdit();
      }
      setMessage('Labor pool deleted.');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to delete labor pool.');
    } finally {
      setSavingLaborPoolId('');
    }
  };

  const selectedModelSourceAircraft = modelAircraftRows.find(row => row.id === modelSourceAircraftId) || null;
  const modelTargetCandidates = modelAircraftRows.filter(row => (
    row.id !== modelSourceAircraftId &&
    (!selectedModelSourceAircraft?.mds || String(row.mds || '').trim().toUpperCase() === String(selectedModelSourceAircraft.mds || '').trim().toUpperCase())
  ));

  const updateModelSourceAircraft = value => {
    setModelSourceAircraftId(value);
    setModelTargetAircraftIds([]);
    setModelPreview(null);
    setSelectedModelSuggestionIds([]);
    setModelSourceRequirementFilter('');
    setModelSourceRequirements([]);
    setMessage('');
    setError('');
    if (value) loadModelSourceRequirements(value);
  };

  const toggleModelTargetAircraft = (aircraftId, checked) => {
    setModelTargetAircraftIds(prev => (
      checked
        ? Array.from(new Set([...prev, aircraftId]))
        : prev.filter(id => id !== aircraftId)
    ));
    setModelPreview(null);
    setSelectedModelSuggestionIds([]);
  };

  const selectModelTargets = location => {
    const ids = modelTargetCandidates
      .filter(row => !location || row.location === location)
      .map(row => row.id);
    setModelTargetAircraftIds(ids);
    setModelPreview(null);
    setSelectedModelSuggestionIds([]);
  };

  const previewModelAircraftPropagation = async () => {
    if (!modelSourceAircraftId) {
      setError('Choose the model aircraft first.');
      return;
    }
    if (!modelTargetAircraftIds.length) {
      setError('Choose at least one target aircraft.');
      return;
    }
    try {
      setModelAircraftWorking('preview');
      setMessage('');
      setError('');
      const res = await api.post('/g081-imports/model-aircraft/preview', {
        ...laborTemplateRequestContext(),
        sourceAircraftId: modelSourceAircraftId,
        sourceRequirementIds: modelSourceRequirementFilter ? [modelSourceRequirementFilter] : [],
        targetAircraftIds: modelTargetAircraftIds,
      });
      const preview = res.data || {};
      setModelPreview(preview);
      setSelectedModelSuggestionIds((preview.suggestions || []).filter(row => row.selected).map(row => row.id));
      const sourceTaskText = modelSourceRequirementFilter ? ` for ${modelRequirementLabel(modelSourceRequirements.find(row => row.id === modelSourceRequirementFilter))}` : '';
      setMessage(`Preview found ${preview.summary?.exactNounMatches || 0} exact noun match${preview.summary?.exactNounMatches === 1 ? '' : 'es'} and ${preview.summary?.manualReviewMatches || 0} manual review candidate${preview.summary?.manualReviewMatches === 1 ? '' : 's'}${sourceTaskText}.`);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to preview model aircraft matches.');
    } finally {
      setModelAircraftWorking('');
    }
  };

  const toggleModelSuggestion = (suggestionId, checked) => {
    setSelectedModelSuggestionIds(prev => (
      checked
        ? Array.from(new Set([...prev, suggestionId]))
        : prev.filter(id => id !== suggestionId)
    ));
  };

  const modelSuggestionMatchesRequirementFilter = (row, filterValue = modelSourceRequirementFilter) => (
    !filterValue || row.sourceRequirement?.id === filterValue
  );

  const selectModelSuggestions = (mode = 'all') => {
    const scopedSuggestions = (modelPreview?.suggestions || []).filter(row => modelSuggestionMatchesRequirementFilter(row));
    setSelectedModelSuggestionIds(scopedSuggestions
      .filter(row => mode !== 'exact' || row.matchType === 'exact_noun')
      .map(row => row.id));
  };

  const updateModelSourceRequirementFilter = value => {
    setModelSourceRequirementFilter(value);
    setModelPreview(null);
    setSelectedModelSuggestionIds([]);
  };

  const applyModelAircraftPropagation = async () => {
    if (modelApplyInFlightRef.current) {
      setMessage('Apply From Tail is already applying the selected matches. Please wait for it to finish.');
      return;
    }
    const selectedSuggestions = (modelPreview?.suggestions || []).filter(row => (
      modelSuggestionMatchesRequirementFilter(row)
      && selectedModelSuggestionIds.includes(row.id)
    ));
    if (!selectedSuggestions.length) {
      setError('Select at least one match to apply.');
      return;
    }
    const selectedJobLabel = modelSourceRequirementFilter
      ? modelRequirementLabel(selectedSuggestions[0]?.sourceRequirement)
      : 'all selected source jobs';
    const coverageText = `, labor loads${modelApplyResources ? ", equipment/facility requirements" : ""}${modelApplyCoverage ? ", and accomplished-by relationships" : ""}`;
    if (!window.confirm(`Apply readiness driver tags${coverageText} for ${selectedJobLabel} to ${selectedSuggestions.length} selected match${selectedSuggestions.length === 1 ? '' : 'es'}?`)) return;
    try {
      modelApplyInFlightRef.current = true;
      setModelAircraftWorking('apply');
      setMessage(`Applying ${selectedSuggestions.length} selected model-aircraft match${selectedSuggestions.length === 1 ? '' : 'es'}...`);
      setError('');
      const res = await api.post('/g081-imports/model-aircraft/apply', {
        ...laborTemplateRequestContext(),
        applyReadiness: true,
        applyLabor: true,
        applyResources: modelApplyResources,
        overwriteResources: modelOverwriteResources,
        applyCoverage: modelApplyCoverage,
        overwriteLabor: modelOverwriteLabor,
        rememberReadiness: modelRememberReadiness,
        readinessLearningScope: modelReadinessLearningScope,
        sourceRequirementIds: modelSourceRequirementFilter ? [modelSourceRequirementFilter] : [],
        matches: selectedSuggestions.map(row => ({
          sourceRequirementId: row.sourceRequirement?.id,
          targetRequirementId: row.targetRequirement?.id,
        })),
      });
      const applied = res.data?.applied || 0;
      const recalculation = res.data?.recalculation || {};
      const pendingRecalculationCount = recalculation.pendingLocations?.length || 0;
      const recalculationMessage = recalculation.deferred && pendingRecalculationCount
        ? ` Crystal Ball recalculation was deferred for ${pendingRecalculationCount} base${pendingRecalculationCount === 1 ? '' : 's'} because this was a large batch.`
        : recalculation.errorLocations?.length
          ? ` The labor/readiness changes were saved, but Crystal Ball recalculation needs to be retried for ${recalculation.errorLocations.length} base${recalculation.errorLocations.length === 1 ? '' : 's'}.`
          : '';
      setModelPreview(null);
      setSelectedModelSuggestionIds([]);
      setModelSourceRequirementFilter('');
      setModelTargetAircraftIds([]);
      const coverageCopied = res.data?.coverageCopied || 0;
      const skippedCoverage = res.data?.skippedCoverage || 0;
      setMessage(`Done: applied ${applied} model match${applied === 1 ? '' : 'es'}${res.data?.skippedLabor ? `; skipped ${res.data.skippedLabor} existing labor load${res.data.skippedLabor === 1 ? '' : 's'}` : ''}${modelApplyCoverage ? `; copied ${coverageCopied} accomplished-by relationship${coverageCopied === 1 ? '' : 's'}${skippedCoverage ? ` and skipped ${skippedCoverage}` : ''}` : ''}.${recalculationMessage} Select targets and preview again to review remaining matches.`);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to apply model aircraft matches.');
    } finally {
      modelApplyInFlightRef.current = false;
      setModelAircraftWorking('');
    }
  };

  const renderModelAircraftPanel = () => {
    const sourceLocations = Array.from(new Set(modelAircraftRows.map(row => row.location).filter(Boolean))).sort();
    const normalizedSourceSearch = modelSourceSearch.trim().toUpperCase();
    const filteredModelSourceRows = modelAircraftRows.filter(row => {
      if (modelSourceLocationFilter !== 'all' && row.location !== modelSourceLocationFilter) return false;
      if (!normalizedSourceSearch) return true;
      return [
        row.tail,
        row.serialNumber,
        row.mds,
        row.location,
        row.status,
      ].some(value => String(value || '').toUpperCase().includes(normalizedSourceSearch));
    });
    const sourceSelectRows = selectedModelSourceAircraft && !filteredModelSourceRows.some(row => row.id === selectedModelSourceAircraft.id)
      ? [selectedModelSourceAircraft, ...filteredModelSourceRows]
      : filteredModelSourceRows;
    const locations = Array.from(new Set(modelTargetCandidates.map(row => row.location).filter(Boolean))).sort();
    const modelSourceRequirementOptions = [...modelSourceRequirements]
      .sort((left, right) => modelRequirementLabel(left).localeCompare(modelRequirementLabel(right), undefined, { numeric: true }));
    const modelSuggestions = modelPreview?.suggestions || [];
    const activeModelSuggestions = modelSuggestions.filter(row => modelSuggestionMatchesRequirementFilter(row));
    const selectedCount = activeModelSuggestions.filter(row => selectedModelSuggestionIds.includes(row.id)).length;
    const exactSuggestions = activeModelSuggestions.filter(row => row.matchType === 'exact_noun').length;
    const manualSuggestions = activeModelSuggestions.filter(row => row.matchType !== 'exact_noun').length;
    return (
      <div className="admin-labor-template-model">
        <section className="admin-labor-model-step">
          <div className="admin-labor-model-step__header">
            <div>
              <strong>1. Choose the reviewed model tail</strong>
              <span>Filter by location first, then pick the aircraft that already has the readiness decisions and labor loads you trust.</span>
            </div>
            <button type="button" className="admin-refresh-button" onClick={loadModelAircraftOptions} disabled={Boolean(modelAircraftWorking)}>
              {modelAircraftWorking === 'options' ? 'Refreshing...' : 'Refresh Tails'}
            </button>
          </div>
          <div className="admin-labor-model-controls">
            <label>
              <span>Model location</span>
              <select value={modelSourceLocationFilter} onChange={event => setModelSourceLocationFilter(event.target.value)}>
                <option value="all">All locations</option>
                {sourceLocations.map(location => <option key={location} value={location}>{location}</option>)}
              </select>
            </label>
            <label>
              <span>Find tail / serial</span>
              <input value={modelSourceSearch} onChange={event => setModelSourceSearch(event.target.value)} placeholder="Type tail, serial, MDS, or status" />
            </label>
            <label className="admin-labor-model-controls__wide">
              <span>Model aircraft</span>
              <select value={modelSourceAircraftId} onChange={event => updateModelSourceAircraft(event.target.value)}>
                <option value="">Choose reviewed tail</option>
                {sourceSelectRows.map(row => (
                  <option key={row.id} value={row.id}>
                    {row.tail || row.serialNumber || 'Tail'} | {row.mds || 'MDS'} | {row.location || 'Location'} | {row.plannedRequirementCount} planned
                  </option>
                ))}
              </select>
            </label>
            <div className="admin-labor-model-summary">
              <strong>{filteredModelSourceRows.length}</strong>
              <span>model choices shown</span>
            </div>
          </div>
        </section>

        {selectedModelSourceAircraft ? (
          <>
            <section className="admin-labor-model-step">
              <div className="admin-labor-model-step__header">
                <div>
                  <strong>2. Select source task and target tails</strong>
                  <span>{modelSourceRequirementFilter ? 'Preview will match one model task only' : 'Preview will match every reviewable task on the model tail'} | {modelTargetAircraftIds.length} target{modelTargetAircraftIds.length === 1 ? '' : 's'} selected{selectedModelSourceAircraft?.mds ? ` | ${selectedModelSourceAircraft.mds}` : ''}</span>
                </div>
              </div>
              <div className="admin-labor-model-controls">
                <label className="scheduled-event-checkbox"><input type="checkbox" checked={modelApplyResources} onChange={event => setModelApplyResources(event.target.checked)} /><span>Copy equipment/facility requirements (assign resources per tail)</span></label>
                <label className="scheduled-event-checkbox"><input type="checkbox" checked={modelOverwriteResources} disabled={!modelApplyResources} onChange={event => setModelOverwriteResources(event.target.checked)} /><span>Overwrite target equipment/facility requirements</span></label>
                <label className="admin-labor-model-controls__wide">
                  <span>Model job / noun</span>
                  <select value={modelSourceRequirementFilter} onChange={event => updateModelSourceRequirementFilter(event.target.value)} disabled={modelSourceRequirementsLoading}>
                    <option value="">{modelSourceRequirementsLoading ? 'Loading model tasks...' : 'All reviewable source tasks'}</option>
                    {modelSourceRequirementOptions.map(requirement => (
                      <option key={requirement.id} value={requirement.id}>
                        {modelRequirementLabel(requirement)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="admin-labor-model-summary">
                  <strong>{modelSourceRequirementsLoading ? '--' : modelSourceRequirementOptions.length}</strong>
                  <span>source task{modelSourceRequirementOptions.length === 1 ? '' : 's'} available</span>
                </div>
              </div>
              <div className="admin-labor-model-actionbar">
                <button type="button" onClick={() => selectModelTargets('')}>Select all same-MDS targets</button>
                {locations.map(location => (
                  <button key={location} type="button" onClick={() => selectModelTargets(location)}>
                    {location}
                  </button>
                ))}
                <button type="button" className="is-secondary" onClick={() => selectModelTargets('__none__')}>Clear targets</button>
              </div>
            <div className="reports-table-shell admin-labor-template-table-shell">
              <table className="reports-table admin-labor-template-table">
                <thead>
                  <tr>
                    <th>Use</th>
                    <th>Tail</th>
                    <th>MDS</th>
                    <th>Location</th>
                    <th>Reviewed Work</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {modelTargetCandidates.map(row => (
                    <tr key={row.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={modelTargetAircraftIds.includes(row.id)}
                          onChange={event => toggleModelTargetAircraft(row.id, event.target.checked)}
                        />
                      </td>
                      <td><strong>{row.tail || '--'}</strong><span>{row.serialNumber || ''}</span></td>
                      <td>{row.mds || '--'}</td>
                      <td>{row.location || '--'}</td>
                      <td>{row.plannedRequirementCount || 0} planned | {row.readinessRequirementCount || 0} readiness</td>
                      <td>{row.status || '--'}</td>
                    </tr>
                  ))}
                  {!modelTargetCandidates.length ? (
                    <tr><td colSpan={6}>Choose a model aircraft with same-MDS target tails in your G081 scope.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            </section>
            <section className="admin-labor-model-step">
              <div className="admin-labor-model-step__header">
                <div>
                  <strong>3. Preview and apply matching work</strong>
                  <span>Preview finds exact 9188-6 noun matches automatically and leaves near matches for manual review.</span>
                </div>
                <button type="button" className="admin-refresh-button" onClick={previewModelAircraftPropagation} disabled={Boolean(modelAircraftWorking) || !modelTargetAircraftIds.length}>
                  {modelAircraftWorking === 'preview' ? 'Previewing...' : 'Preview Matches'}
                </button>
              </div>
              <div className="admin-labor-model-controls">
                <label className="scheduled-event-checkbox">
                  <input type="checkbox" checked={modelOverwriteLabor} onChange={event => setModelOverwriteLabor(event.target.checked)} />
                  <span>Overwrite target labor loads</span>
                </label>
                <label className="scheduled-event-checkbox">
                  <input type="checkbox" checked={modelApplyCoverage} onChange={event => setModelApplyCoverage(event.target.checked)} />
                  <span>Apply accomplished-by relationships</span>
                </label>
                <label className="scheduled-event-checkbox">
                  <input type="checkbox" checked={modelRememberReadiness} onChange={event => setModelRememberReadiness(event.target.checked)} />
                  <span>Remember readiness decisions</span>
                </label>
                <label className="admin-labor-model-controls__wide">
                  <span>Readiness memory scope</span>
                  <select value={modelReadinessLearningScope} onChange={event => setModelReadinessLearningScope(event.target.value)} disabled={!modelRememberReadiness}>
                    <option value="mds">All locations for this MDS</option>
                    <option value="location">Each target location only</option>
                    <option value="enterprise">Enterprise generic</option>
                  </select>
                </label>
              </div>
            </section>
          </>
        ) : null}

        {modelPreview ? (
          <section className="admin-labor-model-step">
            <div className="admin-labor-model-step__header">
              <div>
                <strong>{selectedCount}</strong>
                <span>matches selected | {exactSuggestions} exact noun | {manualSuggestions} manual review{modelSourceRequirementFilter ? ' | one model job' : ''}</span>
              </div>
              <button
                type="button"
                className={modelAircraftWorking === 'apply' ? 'is-applying' : ''}
                onClick={applyModelAircraftPropagation}
                disabled={Boolean(modelAircraftWorking) || !selectedCount}
                aria-busy={modelAircraftWorking === 'apply'}
              >
                {modelAircraftWorking === 'apply' ? `Applying ${selectedCount}...` : 'Apply Selected'}
              </button>
            </div>
            <div className="admin-labor-model-actionbar">
              <button type="button" onClick={() => selectModelSuggestions('all')}>Select all shown</button>
              <button type="button" onClick={() => selectModelSuggestions('exact')}>Exact shown only</button>
              <button type="button" className="is-secondary" onClick={() => setSelectedModelSuggestionIds([])}>Clear matches</button>
            </div>
            <div className="reports-table-shell admin-labor-template-table-shell">
              <table className="reports-table admin-labor-template-table">
                <thead>
                  <tr>
                    <th>Use</th>
                    <th>Match</th>
                    <th>Target Tail</th>
                    <th>Model 9188-6 Noun</th>
                    <th>Target 9188-6 Noun</th>
                    <th>Readiness</th>
                    <th>Accomplished By</th>
                    <th>Labor Load</th>
                  </tr>
                </thead>
                <tbody>
                  {activeModelSuggestions.map(row => {
                    const coverage = row.coverageRelationship;
                    return (
                    <tr key={row.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedModelSuggestionIds.includes(row.id)}
                          onChange={event => toggleModelSuggestion(row.id, event.target.checked)}
                        />
                      </td>
                      <td>
                        <strong>{row.matchType === 'exact_noun' ? 'Exact noun' : 'Manual review'}</strong>
                        <span>{row.reason || ''} | {row.score}</span>
                      </td>
                      <td>
                        <strong>{row.targetAircraft?.tail || '--'}</strong>
                        <span>{row.targetAircraft?.location || ''}</span>
                      </td>
                      <td>
                        <strong>{row.sourceRequirement?.noun || '--'}</strong>
                        <span>{row.sourceRequirement?.wuc || '--'}</span>
                      </td>
                      <td>
                        <strong>{row.targetRequirement?.noun || '--'}</strong>
                        <span>{row.targetRequirement?.wuc || '--'}</span>
                      </td>
                      <td>{row.sourceRequirement?.affectsReadiness === false ? 'No' : 'Yes'}{row.wouldChangeReadiness ? ' | changes target' : ''}</td>
                      <td>
                        {coverage ? (
                          <>
                            <strong>{coverage.wouldApply ? 'Will copy' : 'Skipped'}</strong>
                            <span>{coverage.wouldApply ? coverage.targetCoveringRequirement?.noun || '--' : coverage.message || '--'}</span>
                          </>
                        ) : '--'}
                      </td>
                      <td>
                        {row.sourceRequirement?.unitLaborRequired === false
                          ? `Unit labor exempt${row.sourceRequirement?.unitLaborExemptionReason ? ` | ${row.sourceRequirement.unitLaborExemptionReason}` : ''}`
                          : (row.sourceRequirement?.laborRequirements || []).length
                          ? `${row.sourceRequirement.laborRequirements.length} row${row.sourceRequirement.laborRequirements.length === 1 ? '' : 's'}${row.targetHasLabor ? ' | target has labor' : ''}`
                          : row.sourceRequirement?.estimatedDurationHours
                            ? `${row.sourceRequirement.estimatedDurationHours} hrs`
                            : '--'}
                      </td>
                    </tr>
                    );
                  })}
                  {!activeModelSuggestions.length ? (
                    <tr><td colSpan={8}>{modelSourceRequirementFilter ? 'No exact noun or reviewable 9188-6 matches were found for the selected model job.' : 'No exact noun or reviewable 9188-6 matches were found for the selected targets.'}</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}
      </div>
    );
  };

  const renderLaborPoolEditForm = () => {
    const isEnterprise = laborPoolDraft.profileScope === 'enterprise';
    const locationOptions = Array.from(new Set([selectedBase, user?.location, ...laborTemplateLocationOptions, ...laborPools.map(pool => pool.location)].map(value => String(value || '').trim()).filter(Boolean))).sort();
    const eligibleSet = new Set(laborPoolDraft.eligibleShops || []);
    const eligibleOptions = Array.from(new Set([...(shopOptions || MILITARY_SHOP_OPTIONS), ...(laborPoolDraft.eligibleShops || [])].filter(Boolean))).sort();
    const saveLabel = savingLaborPoolId ? 'Saving...' : editingLaborPoolId === 'new' ? 'Create pool' : 'Save pool';
    return (
      <form className="admin-labor-template-edit-form" onSubmit={saveLaborPool}>
        <label>
          <span>Pool name</span>
          <input value={laborPoolDraft.name} onChange={event => updateLaborPoolDraft('name', event.target.value)} />
        </label>
        <label>
          <span>Scope</span>
          <select value={laborPoolDraft.profileScope} onChange={event => updateLaborPoolDraft('profileScope', event.target.value)}>
            {user?.is_superuser ? <option value="enterprise">Enterprise-wide</option> : null}
            <option value="location">Base-specific</option>
          </select>
        </label>
        <label>
          <span>Location</span>
          <input list="admin-labor-pool-location-options" disabled={isEnterprise || !user?.is_superuser} value={isEnterprise ? 'Enterprise-wide' : laborPoolDraft.location} onChange={event => updateLaborPoolDraft('location', event.target.value)} />
        </label>
        <label className="admin-labor-template-wide-field">
          <span>Eligible shops</span>
          <select
            multiple
            size={Math.min(8, Math.max(4, eligibleOptions.length))}
            value={laborPoolDraft.eligibleShops}
            onChange={event => updateLaborPoolDraft('eligibleShops', Array.from(event.target.selectedOptions).map(option => option.value))}
          >
            {eligibleOptions.map(shop => (
              <option key={shop} value={shop}>{shop}</option>
            ))}
          </select>
        </label>
        <label className="scheduled-event-checkbox admin-labor-template-active-toggle">
          <input type="checkbox" checked={laborPoolDraft.active} onChange={event => updateLaborPoolDraft('active', event.target.checked)} />
          <span>Active</span>
        </label>
        <label className="admin-labor-template-wide-field">
          <span>Notes</span>
          <textarea value={laborPoolDraft.notes} onChange={event => updateLaborPoolDraft('notes', event.target.value)} />
        </label>
        <div className="admin-labor-template-edit-actions">
          <button type="submit" disabled={Boolean(savingLaborPoolId) || !laborPoolDraft.name || !eligibleSet.size}>{saveLabel}</button>
          <button type="button" className="profile-modal__secondary" onClick={cancelLaborPoolEdit} disabled={Boolean(savingLaborPoolId)}>Cancel</button>
        </div>
        <datalist id="admin-labor-pool-location-options">
          {locationOptions.map(option => <option key={option} value={option} />)}
        </datalist>
      </form>
    );
  };

  if (!canUseAdmin) {
    return (
      <div className="admin-tab">
        <section className="reports-panel admin-panel">
          <h3>Admin</h3>
          <p className="reports-empty">Only siteAdmin, Commander, Management, Production, or superuser accounts can use the base admin panel.</p>
        </section>
      </div>
    );
  }

  const hiddenRecapAddOns = new Set(['personnel_sms_alerts']);
  const enabledAddOns = Object.entries(subscription?.integrationFlags || {})
    .filter(([, enabled]) => enabled)
    .filter(([key]) => !hiddenRecapAddOns.has(key))
    .map(([key]) => key);
  const availableFlags = subscription?.availableIntegrationFlags || [];
  const flagLabels = Object.fromEntries(availableFlags.map(option => [option.key, option.label]));
  const filteredUsers = users.filter(row => {
    if (userStatusFilter === 'active') return row.isActive;
    if (userStatusFilter === 'deactivated') return !row.isActive;
    return true;
  });
  const plans = billingCatalog?.plans || {};
  const standardPlan = plans.standard_monthly || null;
  const standardAnnualPlan = plans.standard_annual_promo || null;
  const selectedStandardPlan = billingCycle === 'annual' ? (standardAnnualPlan || standardPlan) : standardPlan;
  const premiumMonthlyPlan = plans.premium_monthly || null;
  const premiumAnnualPlan = plans.premium_annual_promo || null;
  const selectedPremiumPlan = billingCycle === 'annual' ? premiumAnnualPlan : premiumMonthlyPlan;
  const addOns = billingCatalog?.addOns || {};
  const visibleAddOns = Object.values(addOns).filter(addOn => addOn?.visible !== false);
  const currentPlanKey = subscription?.billingPlanKey || '';
  const hasStripeCustomer = Boolean(subscription?.providerCustomerId);
  const stripeConfigured = Boolean(billingCatalog?.configured);
  const activeSeatCount = Number(subscription?.activeUserCount || 0);
  const seatLimit = Number(subscription?.seatLimit || 0);
  const remainingSeats = seatLimit > 0 ? Math.max(seatLimit - activeSeatCount, 0) : null;
  const showSeatCapacityWarning = remainingSeats !== null && remainingSeats <= 1;
  const seatCapacityWarningText = remainingSeats === 0
    ? `No licenses remain for ${selectedBase || user?.location || 'this location'}. New registrations will be blocked until a seat is freed or the plan is upgraded.`
    : `Only 1 license remains for ${selectedBase || user?.location || 'this location'}. You are near capacity for registered users.`;
  const activeSiteAdmins = users.filter(row => row.isActive && normalizeRoleValue(row.role) === 'siteadmin');
  const showSiteAdminBackupWarning = canManageUsers && activeSiteAdmins.length === 1;
  const onlySiteAdminEmail = activeSiteAdmins[0]?.email || 'the current siteAdmin';
  const supportAccessEnabled = Boolean(subscription?.mfeSupportAccessEnabled);
  const canManageSupportAccess = Boolean(subscription?.canManageSupportAccess);
  const laborTemplateLocationOptions = Array.from(new Set(laborTemplates.map(row => String(row.location || '').trim()).filter(Boolean))).sort();
  const renderSupportPanel = () => (
    <section className="reports-panel admin-panel admin-support-panel">
      <div className="reports-panel__header">
        <div>
          <h3>Support</h3>
          <span>Contact MFE support or grant temporary support access for {selectedBase || user?.location || 'this location'}.</span>
        </div>
        <span className={`admin-status-pill ${supportAccessEnabled ? 'is-active' : 'is-inactive'}`}>
          {supportAccessEnabled ? 'Support Access Enabled' : 'Support Access Off'}
        </span>
      </div>

      {!subscription ? (
        <p className="reports-empty">{isLoading ? 'Loading support settings...' : 'Support settings are not available.'}</p>
      ) : (
        <>
          <div className="admin-support-contact-card">
            <div>
              <span>MFE Support Contact</span>
              <strong>Email info@mfe.llc for help.</strong>
              <small>Use this for billing questions, support requests, trial extensions, upgrades, and customer assistance.</small>
            </div>
            <a
              className="admin-upgrade-link"
              href={`mailto:info@mfe.llc?subject=${encodeURIComponent(`FORGE support request for ${selectedBase || user?.location || 'selected location'}`)}`}
            >
              Email MFE Support
            </a>
          </div>

          <div className="admin-billing-subpanel admin-support-access-panel">
            <div>
              <h4>MFE Support Access</h4>
              <p>
                Allows the MFE support account ({subscription.mfeOwnerSupportEmail || 'support@mfe.llc'}) to assist this location without listing that account as a billable unit user.
              </p>
            </div>
            <div className="admin-support-access-status">
              <span className={`admin-status-pill ${supportAccessEnabled ? 'is-active' : 'is-inactive'}`}>
                {supportAccessEnabled ? 'Enabled' : 'Not enabled'}
              </span>
              <span>
                {supportAccessEnabled
                  ? `Granted by ${subscription.mfeSupportAccessGrantedBy || '--'} ${formatLocalDateTime(subscription.mfeSupportAccessGrantedAt) || ''}`
                  : 'Site leadership has not granted support access.'}
              </span>
              {subscription.mfeSupportAccessReason ? <small>{subscription.mfeSupportAccessReason}</small> : null}
              {canManageSupportAccess ? (
                <button
                  type="button"
                  className={supportAccessEnabled ? 'profile-modal__secondary' : ''}
                  onClick={() => updateSupportAccess(!supportAccessEnabled)}
                  disabled={billingAction === 'support-enable' || billingAction === 'support-revoke'}
                >
                  {billingAction === 'support-enable' || billingAction === 'support-revoke'
                    ? 'Updating...'
                    : supportAccessEnabled ? 'Revoke MFE Support Access' : 'Allow MFE Support Access'}
                </button>
              ) : (
                <span className="reports-empty">Only siteAdmin or superuser accounts can change support access.</span>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );

  return (
    <div className="admin-tab">
      <header className="admin-hero">
        <div>
          <div className="reports-eyebrow">Base Admin</div>
          <h2>Site Administration</h2>
          <p>Manage users and view subscription readiness for {selectedBase || user?.location || 'this location'}.</p>
        </div>
        <button type="button" className="admin-refresh-button" onClick={loadAdminData} disabled={isLoading}>
          {isLoading ? 'Refreshing...' : 'Refresh Admin Data'}
        </button>
      </header>

      {message ? <div className="admin-message is-success">{message}</div> : null}
      {error ? <div className="admin-message is-error">{error}</div> : null}
      {showSeatCapacityWarning ? (
        <div className="admin-message is-warning">
          <strong>Seat capacity warning:</strong> {seatCapacityWarningText} Upgrade from the Subscription Recap page or contact{' '}
          <a href="mailto:info@mfe.llc?subject=FORGE seat capacity upgrade">info@mfe.llc</a> for options.
        </div>
      ) : null}
      {showSiteAdminBackupWarning ? (
        <div className="admin-message is-warning admin-siteadmin-backup-warning">
          <strong>siteAdmin backup needed:</strong> {onlySiteAdminEmail} is the only active siteAdmin for {selectedBase || user?.location || 'this location'}.
          Promote at least one trusted backup siteAdmin so the location can still manage users if that account leaves, loses access, or changes role.
        </div>
      ) : null}

      <div className="admin-layout">
        <aside className="admin-side-menu" aria-label="Admin sections">
          {canManageUsers ? (
            <button
              type="button"
              className={activeSection === 'users' ? 'is-active' : ''}
              onClick={() => setActiveSection('users')}
            >
              <span>User Management</span>
              <small>Roles, shops, activation</small>
            </button>
          ) : null}
          {canManageUsers ? (
            <button
              type="button"
              className={activeSection === 'transfers' ? 'is-active' : ''}
              onClick={() => setActiveSection('transfers')}
            >
              <span>Location Transfers</span>
              <small>{transferRequests.length} pending</small>
            </button>
          ) : null}
          <button
            type="button"
            className={activeSection === 'subscription' ? 'is-active' : ''}
            onClick={() => {
              setActiveSection('subscription');
              loadSubscriptionForLocation(selectedBase).catch(err => {
                setError(err.response?.data?.message || 'Unable to load subscription recap.');
              });
            }}
          >
            <span>Subscription Recap</span>
            <small>Plan, seats, add-ons</small>
          </button>
          <button
            type="button"
            className={activeSection === 'support' ? 'is-active' : ''}
            onClick={() => {
              setActiveSection('support');
              loadSubscriptionForLocation(selectedBase).catch(err => {
                setError(err.response?.data?.message || 'Unable to load support settings.');
              });
            }}
          >
            <span>Support</span>
            <small>Contact, grant access</small>
          </button>
          {canManageTemplates ? (
            <button
              type="button"
              className={activeSection === 'laborTemplates' ? 'is-active' : ''}
              onClick={() => setActiveSection('laborTemplates')}
            >
              <span>MDS Job Templates</span>
              <small>Labor, equipment, and facilities per job</small>
            </button>
          ) : null}
          <button type="button" className={activeSection === 'resources' ? 'is-active' : ''} onClick={() => setActiveSection('resources')}><span>Equipment &amp; Facilities</span><small>Site inventory and enterprise catalog</small></button>
        </aside>

        <div className="admin-content">
          {activeSection === 'resources' ? (
            <ResourceWorkspace />
          ) : activeSection === 'users' ? (
            <section className="reports-panel admin-panel">
              <div className="reports-panel__header">
                <div>
                  <h3>User Management</h3>
                  <span>Deactivate users who leave the unit, or adjust role/shop access.</span>
                </div>
                <div className="admin-user-filter">
                  <label htmlFor="admin-user-status-filter">Show</label>
                  <select
                    id="admin-user-status-filter"
                    value={userStatusFilter}
                    onChange={event => setUserStatusFilter(event.target.value)}
                  >
                    <option value="active">Active users</option>
                    <option value="deactivated">Deactivated users</option>
                    <option value="all">All users</option>
                  </select>
                  <span>{filteredUsers.length} of {users.length}</span>
                </div>
              </div>
              <div className="reports-table-shell">
                <table className="reports-table admin-users-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Status</th>
                      <th>Location Sharing</th>
                      <th>Personnel Link</th>
                      <th>Role</th>
                      <th>Shop</th>
                      <th>User Type</th>
                      <th>Terms</th>
                      <th>Deactivation</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length > 0 ? filteredUsers.map(row => {
                      const draft = drafts[row.id] || {};
                      const isSelf = row.email === user.email;
                      return (
                        <tr key={row.id} className={row.isActive ? '' : 'is-inactive-user'}>
                          <td>
                            <strong>{row.email}</strong>
                            <span>{row.location}</span>
                          </td>
                          <td>
                            <span className={`admin-status-pill ${row.isActive ? 'is-active' : 'is-inactive'}`}>
                              {row.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="admin-location-sharing-cell">
                            <span className={`admin-status-pill is-location-${row.locationSharingStatus || 'unknown'}`}>
                              {row.locationSharingLabel || 'Unknown'}
                            </span>
                            <span>{formatLocationSharingDetail(row)}</span>
                          </td>
                          <td>
                            {row.personnelLinked ? (
                              <>
                                <span className="admin-link-pill is-linked" title={`Emp # ${row.personnelEmpNum || '--'}`}>
                                  Linked: {row.personnelName || row.personnelEmpNum || 'Personnel'}
                                </span>
                                {row.roleShopConflict ? (
                                  <span
                                    className="admin-link-pill is-conflict"
                                    title={`Personnel has role ${row.personnelRole || '--'} and shop ${row.personnelShop || '--'}. Save here to sync from account master.`}
                                  >
                                    Sync needed
                                  </span>
                                ) : null}
                              </>
                            ) : (
                              <span className="admin-link-pill is-unlinked">
                                No personnel match
                              </span>
                            )}
                          </td>
                          <td>
                            <select
                              value={draft.role ?? row.role ?? ''}
                              onChange={event => updateDraft(row.id, 'role', event.target.value)}
                              disabled={row.isSuperuser && !user.is_superuser}
                            >
                              <option value="">No Role</option>
                              {roleOptions.map(role => (
                                <option key={role} value={role}>{role}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              value={draft.shop ?? row.shop ?? ''}
                              onChange={event => updateDraft(row.id, 'shop', event.target.value)}
                              disabled={row.isSuperuser && !user.is_superuser}
                            >
                              <option value="">No Shop</option>
                              {shopOptions.map(shop => (
                                <option key={shop} value={shop}>{shop}</option>
                              ))}
                            </select>
                          </td>
                          <td>{row.userType || '--'}</td>
                          <td>
                            {row.termsAcceptedAt ? (
                              <>
                                <strong>{row.termsVersion || 'Accepted'}</strong>
                                <span>{formatLocalDateTime(row.termsAcceptedAt)}</span>
                                <span title={row.termsAcceptanceUserAgent || ''}>{row.termsAcceptanceIp || ''}</span>
                              </>
                            ) : (
                              <span className="admin-link-pill is-conflict">Not recorded</span>
                            )}
                          </td>
                          <td>
                            {row.deactivatedAt ? (
                              <>
                                <strong>{formatLocalDateTime(row.deactivatedAt)}</strong>
                                <span>{row.deactivatedBy || ''}</span>
                                <span>{row.deactivationReason || ''}</span>
                              </>
                            ) : '--'}
                          </td>
                          <td>
                            <div className="admin-user-actions">
                              <button
                                type="button"
                                onClick={() => saveUser(row)}
                                disabled={savingUserId === row.id}
                              >
                                Save
                              </button>
                              {row.isActive ? (
                                <button
                                  type="button"
                                  className="is-danger"
                                  onClick={() => setUserActive(row, false)}
                                  disabled={savingUserId === row.id || isSelf}
                                  title={isSelf ? 'You cannot deactivate your own account.' : 'Deactivate login access'}
                                >
                                  Deactivate
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="is-restore"
                                  onClick={() => setUserActive(row, true)}
                                  disabled={savingUserId === row.id}
                                >
                                  Reactivate
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={10} className="reports-empty">
                          {isLoading ? 'Loading users...' : 'No users match the selected status filter.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          ) : activeSection === 'transfers' ? (
            <section className="reports-panel admin-panel">
              <div className="reports-panel__header">
                <div>
                  <h3>Location Transfer Requests</h3>
                  <span>Approve inbound PCS, deployment, or reassignment requests for {selectedBase || user?.location || 'this location'}.</span>
                </div>
                <span className="admin-status-pill is-active">{transferRequests.length} Pending</span>
              </div>
              <div className="reports-table-shell">
                <table className="reports-table admin-transfer-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Losing Location</th>
                      <th>Requested Access</th>
                      <th>Note</th>
                      <th>Approve As</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transferRequests.length > 0 ? transferRequests.map(row => {
                      const draft = transferDrafts[row.id] || {};
                      return (
                        <tr key={row.id}>
                          <td>
                            <strong>{row.email}</strong>
                            <span>{formatLocalDateTime(row.requestedAt)}</span>
                          </td>
                          <td>{row.fromLocation}</td>
                          <td>
                            <strong>{row.requestedRole || 'No role'}</strong>
                            <span>{row.requestedShop || 'No shop'}</span>
                          </td>
                          <td>{row.reason || '--'}</td>
                          <td>
                            <div className="admin-transfer-access-draft">
                              <select
                                value={draft.role ?? row.requestedRole ?? ''}
                                onChange={event => updateTransferDraft(row.id, 'role', event.target.value)}
                              >
                                <option value="">No Role</option>
                                {roleOptions.map(role => (
                                  <option key={role} value={role}>{role}</option>
                                ))}
                              </select>
                              <select
                                value={draft.shop ?? row.requestedShop ?? ''}
                                onChange={event => updateTransferDraft(row.id, 'shop', event.target.value)}
                              >
                                <option value="">No Shop</option>
                                {shopOptions.map(shop => (
                                  <option key={shop} value={shop}>{shop}</option>
                                ))}
                              </select>
                              <input
                                type="text"
                                maxLength={500}
                                placeholder="Review note"
                                value={draft.reviewNote || ''}
                                onChange={event => updateTransferDraft(row.id, 'reviewNote', event.target.value)}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="admin-user-actions">
                              <button
                                type="button"
                                onClick={() => reviewTransferRequest(row, 'approve')}
                                disabled={reviewingTransferId === row.id}
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                className="is-danger"
                                onClick={() => reviewTransferRequest(row, 'reject')}
                                disabled={reviewingTransferId === row.id}
                              >
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={6} className="reports-empty">
                          {isLoading ? 'Loading transfer requests...' : 'No pending transfer requests for this location.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          ) : activeSection === 'support' ? (
            renderSupportPanel()
          ) : activeSection === 'laborTemplates' && canManageTemplates ? (
            <section className="reports-panel admin-panel admin-labor-template-panel">
              <div className="reports-panel__header">
                <div>
                  <h3>MDS Job Templates</h3>
                  <span>Manage task labor rows and reusable equipment/facility requirements used by Status, ACFT Plans, and Crystal Ball.</span>
                </div>
                <div className="admin-labor-template-header-actions">
                  {laborTemplateSubsection === 'pools' ? (
                    <button
                      type="button"
                      className="admin-refresh-button"
                      onClick={startNewLaborPool}
                      disabled={Boolean(savingLaborPoolId)}
                    >
                      Create Pool
                    </button>
                  ) : null}
                  {laborTemplateSubsection === 'pools' ? <button type="button" className="admin-refresh-button" onClick={loadLaborTemplateRepository} disabled={laborTemplatesLoading}>
                    {laborTemplatesLoading ? 'Refreshing...' : 'Refresh Pools'}
                  </button> : null}
                </div>
              </div>

              <div className="admin-labor-template-tabs">
                <button type="button" className={laborTemplateSubsection === 'templates' ? 'is-active' : ''} onClick={() => setLaborTemplateSubsection('templates')}>
                  Templates
                </button>
                <button type="button" className={laborTemplateSubsection === 'pools' ? 'is-active' : ''} onClick={() => setLaborTemplateSubsection('pools')}>
                  Labor Pools
                </button>
                <button type="button" className={laborTemplateSubsection === 'review' ? 'is-active' : ''} onClick={() => setLaborTemplateSubsection('review')}>
                  Enterprise Proposals
                </button>
                {user?.is_superuser ? <button type="button" className={laborTemplateSubsection === 'model' ? 'is-active' : ''} onClick={() => setLaborTemplateSubsection('model')}>
                  Apply From Tail
                </button> : null}
              </div>

              {['templates', 'review'].includes(laborTemplateSubsection) ? (
                <MdsTaskTemplates laborPools={laborPools} onSaved={loadLaborTemplateRepository} view={laborTemplateSubsection} onViewChange={setLaborTemplateSubsection} />
              ) : laborTemplateSubsection === 'pools' ? (
                <div className="reports-table-shell admin-labor-template-table-shell">
                  <table className="reports-table admin-labor-template-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Location / Scope</th>
                        <th>Eligible Shops</th>
                        <th>Status</th>
                        <th>Updated</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editingLaborPoolId === 'new' ? (
                        <tr className="admin-labor-template-edit-row">
                          <td colSpan={6}>{renderLaborPoolEditForm()}</td>
                        </tr>
                      ) : null}
                      {laborPools.map(row => {
                        const rowLocation = String(row.location || '').trim();
                        if (editingLaborPoolId === row.id) {
                          return (
                            <tr key={row.id} className="admin-labor-template-edit-row">
                              <td colSpan={6}>{renderLaborPoolEditForm()}</td>
                            </tr>
                          );
                        }
                        return (
                          <tr key={row.id} className={row.active === false ? 'is-inactive-template' : ''}>
                            <td>
                              <strong>{row.name || 'Untitled pool'}</strong>
                              {row.notes ? <span>{row.notes}</span> : null}
                            </td>
                            <td>
                              <span className={`admin-template-scope-pill ${rowLocation ? 'is-location' : 'is-enterprise'}`}>
                                {rowLocation || 'Enterprise-wide'}
                              </span>
                            </td>
                            <td>{(row.eligibleShops || []).join(', ') || '--'}</td>
                            <td>
                              <span className={`admin-status-pill ${row.active === false ? 'is-inactive' : 'is-active'}`}>
                                {row.active === false ? 'Inactive' : 'Active'}
                              </span>
                            </td>
                            <td>{formatLocalDateTime(row.updatedAt) || '--'}</td>
                            <td>
                              <div className="admin-labor-template-row-actions">
                                <button type="button" className="admin-owner-action" onClick={() => startEditLaborPool(row)} disabled={Boolean(savingLaborPoolId) || (!user?.is_superuser && rowLocation !== user?.location)}>
                                  Edit
                                </button>
                                <button type="button" className="admin-owner-action is-danger" onClick={() => deleteLaborPool(row)} disabled={savingLaborPoolId === row.id || (!user?.is_superuser && rowLocation !== user?.location)}>
                                  {savingLaborPoolId === row.id ? 'Deleting...' : 'Delete'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {!laborPools.length ? (
                        <tr>
                          <td colSpan={6}>{laborTemplatesLoading ? 'Loading labor pools...' : 'No labor pools have been added yet.'}</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              ) : (
                renderModelAircraftPanel()
              )}
            </section>
          ) : (
            <section className="reports-panel admin-panel admin-billing-panel">
              <div className="reports-panel__header">
                <div>
                  <h3>Subscription Recap</h3>
                  <span>Current plan, seats, billing contact, and enabled add-ons for this location.</span>
                </div>
                <span className={`admin-status-pill is-${subscription?.subscriptionStatus || 'inactive'}`}>
                  {subscription?.subscriptionStatus || 'Not loaded'}
                </span>
              </div>

              {!subscription ? (
                <p className="reports-empty">{isLoading ? 'Loading subscription recap...' : 'Subscription recap is not available.'}</p>
              ) : (
                <>
                  <div className="admin-billing-note">
                    MFE manages billing, contracts, and feature entitlements from the MFE Platform Owner Portal. Site admins can use this page to understand their current plan and request upgrades.
                  </div>

                  <div className="admin-billing-summary">
                    <div>
                      <span>Active Users</span>
                      <strong>{subscription.activeUserCount || 0}</strong>
                    </div>
                    <div>
                      <span>Seat Limit</span>
                      <strong>{subscription.seatLimit || 0}</strong>
                    </div>
                    <div>
                      <span>Plan</span>
                      <strong>{subscription.displayPlanName || subscription.planName || '--'}</strong>
                    </div>
                    <div>
                      <span>Monthly Price</span>
                      <strong>{subscription.pricePerMonthCents ? `$${centsToDollars(subscription.pricePerMonthCents)}` : '--'}</strong>
                    </div>
                  </div>

                  <div className="admin-subscription-recap-grid">
                    <div>
                      <span>Billing Owner</span>
                      <strong>{subscription.billingOwnerEmail || '--'}</strong>
                    </div>
                    <div>
                      <span>Billing Contact</span>
                      <strong>{subscription.billingContactEmail || '--'}</strong>
                    </div>
                    <div>
                      <span>Trial Ends</span>
                      <strong>{formatLocalDateTime(subscription.trialEndsAt) || '--'}</strong>
                    </div>
                    <div>
                      <span>Current Period Ends</span>
                      <strong>{formatLocalDateTime(subscription.currentPeriodEndsAt) || '--'}</strong>
                    </div>
                    <div>
                      <span>AI Access</span>
                      <strong>{subscription.aiEnabled ? `${subscription.aiProvider || 'AI'} enabled` : 'Off'}</strong>
                    </div>
                    <div>
                      <span>AI Usage This Month</span>
                      <strong>
                        {subscription.aiMonthlyUsage?.requestCount ?? 0}
                        {subscription.aiMonthlyRequestLimit ? ` / ${subscription.aiMonthlyRequestLimit}` : ' / unlimited'}
                      </strong>
                      <span>
                        {(subscription.aiMonthlyUsage?.totalTokens || 0).toLocaleString()} tokens
                        {subscription.aiMonthlyTokenLimit ? ` / ${Number(subscription.aiMonthlyTokenLimit).toLocaleString()}` : ' / unlimited'}
                      </span>
                    </div>
                    <div>
                      <span>Deployment Mode</span>
                      <strong>{formatDeploymentMode(subscription.deploymentMode)}</strong>
                    </div>
                  </div>

                  <div className="admin-billing-subpanel">
                    <div>
                      <h4>Enabled Add-Ons</h4>
                      <p>These are controlled by the subscription and contract for this location.</p>
                    </div>
                    <div className="admin-integration-grid">
                      {enabledAddOns.length > 0 ? enabledAddOns.map(key => (
                        <span key={key} className="admin-integration-toggle is-readonly">
                          {flagLabels[key] || key}
                        </span>
                      )) : (
                        <span className="reports-empty">No add-ons are currently enabled.</span>
                      )}
                    </div>
                  </div>

                  <div className="admin-billing-subpanel admin-plan-comparison-panel">
                    <div className="admin-plan-comparison-header">
                      <div>
                        <h4>Plans And Premium Features</h4>
                        <p>Customers can upgrade, manage billing, and compare the core Standard plan against premium automation, AI, and Weather features.</p>
                      </div>
                      <div className="admin-billing-cycle-toggle" role="group" aria-label="Billing cadence">
                        <button
                          type="button"
                          className={billingCycle === 'monthly' ? 'is-active' : ''}
                          onClick={() => setBillingCycle('monthly')}
                        >
                          Monthly
                        </button>
                        <button
                          type="button"
                          className={billingCycle === 'annual' ? 'is-active' : ''}
                          onClick={() => setBillingCycle('annual')}
                        >
                          Annual Prepay
                        </button>
                      </div>
                    </div>

                    {!stripeConfigured ? (
                      <div className="admin-billing-note is-warning">
                        Payment links are not active yet because Stripe is not fully configured on the backend. Add `STRIPE_SECRET_KEY` and `STRIPE_WEBHOOK_SECRET` when ready to accept payments.
                      </div>
                    ) : null}

                    <div className="admin-plan-card-grid">
                      {selectedStandardPlan ? (
                        <article className={`admin-plan-card ${currentPlanKey?.startsWith('standard') ? 'is-current' : ''}`}>
                          <div className="admin-plan-card__top">
                            <span>Standard</span>
                            {billingCycle === 'annual' && selectedStandardPlan.billingCadence === 'annual_prepaid' ? (
                              <>
                                <strong>
                                  <em>{formatMonthlyPrice(selectedStandardPlan.regularMonthlyCents)}</em>
                                  {formatMonthlyPrice(selectedStandardPlan.monthlyEquivalentCents)}
                                </strong>
                                <small>
                                  Annual prepay, billed {formatCurrencyFromCents(selectedStandardPlan.priceCents)} for {selectedStandardPlan.months || 12} months
                                </small>
                                {selectedStandardPlan.discountPercent ? (
                                  <b className="admin-plan-card__discount">Limited-time {selectedStandardPlan.discountPercent}% discount</b>
                                ) : null}
                              </>
                            ) : (
                              <>
                                <strong>{formatMonthlyPrice(selectedStandardPlan.monthlyEquivalentCents)}</strong>
                                <small>Monthly operations package</small>
                              </>
                            )}
                          </div>
                          <ul>
                            {(selectedStandardPlan.features || []).map(feature => (
                              <li key={feature}>{feature}</li>
                            ))}
                          </ul>
                          {currentPlanKey === selectedStandardPlan.key ? (
                            <button type="button" disabled>Current Plan</button>
                          ) : hasStripeCustomer ? (
                            <button type="button" onClick={openBillingPortal} disabled={billingAction === 'portal'}>
                              {billingAction === 'portal' ? 'Opening...' : 'Switch / Manage Billing'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => startBillingCheckout(selectedStandardPlan.key)}
                              disabled={!stripeConfigured || billingAction === selectedStandardPlan.key || selectedStandardPlan.promoActive === false}
                            >
                              {billingAction === selectedStandardPlan.key ? 'Opening...' : 'Start Standard'}
                            </button>
                          )}
                        </article>
                      ) : null}

                      {selectedPremiumPlan ? (
                        <article className={`admin-plan-card admin-plan-card--premium ${currentPlanKey?.startsWith('premium') ? 'is-current' : ''}`}>
                          <div className="admin-plan-card__top">
                            <span>Premium</span>
                            {billingCycle === 'annual' ? (
                              <>
                                <strong>
                                  <em>{formatMonthlyPrice(selectedPremiumPlan.regularMonthlyCents)}</em>
                                  {formatMonthlyPrice(selectedPremiumPlan.monthlyEquivalentCents)}
                                </strong>
                                <small>
                                  Limited-time annual prepay, billed {formatCurrencyFromCents(selectedPremiumPlan.priceCents)} for {selectedPremiumPlan.months || 12} months
                                </small>
                                {selectedPremiumPlan.discountPercent ? (
                                  <b className="admin-plan-card__discount">Limited-time {selectedPremiumPlan.discountPercent}% discount</b>
                                ) : null}
                              </>
                            ) : (
                              <>
                                <strong>{formatMonthlyPrice(selectedPremiumPlan.monthlyEquivalentCents)}</strong>
                                <small>No annual prepay required</small>
                              </>
                            )}
                          </div>
                          <ul>
                            {(selectedPremiumPlan.features || []).map(feature => (
                              <li key={feature}>{feature}</li>
                            ))}
                          </ul>
                          {currentPlanKey === selectedPremiumPlan.key ? (
                            <button type="button" disabled>Current Plan</button>
                          ) : hasStripeCustomer ? (
                            <button type="button" onClick={openBillingPortal} disabled={billingAction === 'portal'}>
                              {billingAction === 'portal' ? 'Opening...' : 'Upgrade / Manage Billing'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => startBillingCheckout(selectedPremiumPlan.key)}
                              disabled={!stripeConfigured || billingAction === selectedPremiumPlan.key}
                            >
                              {billingAction === selectedPremiumPlan.key ? 'Opening...' : 'Upgrade To Premium'}
                            </button>
                          )}
                        </article>
                      ) : null}
                    </div>

                    {hasStripeCustomer ? (
                      <div className="admin-billing-actions admin-billing-actions--inline">
                        <button type="button" onClick={openBillingPortal} disabled={billingAction === 'portal'}>
                          {billingAction === 'portal' ? 'Opening Billing Portal...' : 'Manage Payment Method / Invoices'}
                        </button>
                      </div>
                    ) : null}
                  </div>

                  {visibleAddOns.length > 0 ? (
                    <div className="admin-billing-subpanel admin-plan-comparison-panel">
                      <div className="admin-plan-comparison-header">
                        <div>
                          <h4>A La Carte Add-Ons</h4>
                          <p>Add capacity or optional features without changing the main plan tier.</p>
                        </div>
                      </div>
                      <div className="admin-plan-card-grid admin-plan-card-grid--addon">
                        {visibleAddOns.map(addOn => {
                          const actionText = addOn.addOnType === 'seats'
                            ? `Add ${addOn.seatCount || 25} Seats`
                            : addOn.displayName ? `Add ${addOn.displayName}` : 'Add Pack';
                          return (
                            <article key={addOn.key} className="admin-plan-card upgrade-addon-card">
                              <div className="admin-plan-card__top">
                                <span>A La Carte Add-On</span>
                                <strong>{formatMonthlyPrice(addOn.monthlyEquivalentCents)}</strong>
                                <small>{addOn.displayName || 'Optional add-on'}</small>
                              </div>
                              <ul>
                                {(addOn.features || []).map(feature => (
                                  <li key={feature}>{feature}</li>
                                ))}
                              </ul>
                              <button
                                type="button"
                                onClick={() => startBillingCheckout(addOn.key)}
                                disabled={!stripeConfigured || billingAction === addOn.key}
                              >
                                {billingAction === addOn.key ? 'Opening...' : actionText}
                              </button>
                            </article>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  <div className="admin-billing-actions">
                    <a
                      className="admin-upgrade-link"
                      href={`mailto:info@mfe.llc?subject=${encodeURIComponent(`FORGE subscription request for ${selectedBase}`)}`}
                    >
                      Request Upgrade Or Billing Help
                    </a>
                  </div>
                  <div className="admin-billing-note is-warning">
                    Enterprise customers, group purchases, wing-level purchases, or multi-location contracts should contact{' '}
                    <a href="mailto:info@mfe.llc?subject=FORGE Enterprise or group purchase contracting">info@mfe.llc</a>{' '}
                    for contracting and custom pricing.
                  </div>
                </>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
